/**
 * Helpers for "Inspect finished model": reading part IDs from a Blender GLB.
 *
 * The finish pipeline (pipeline/blender/lib/geometry.py, export.py) sets `obj['partId']`
 * (plus role, label, …) on every cabinet part; the glTF exporter writes these as node
 * `extras`, which GLTFLoader copies into `object.userData`. Staging objects are named
 * `staging.*` (e.g. `staging.floor`). GLTFLoader sanitises `object.name` (dots are
 * removed: `stagingfloor`) but keeps the original in `userData.name`.
 *
 * Pure (no DOM): safe to run in Node.
 */
import * as THREE from 'three';

export interface PartNodes {
  /** partId → part nodes (more than one when the GLB repeats an id). */
  parts: Map<string, THREE.Object3D[]>;
  /** Each part id once, in document order. */
  partIds: string[];
  /** Ids that appear on more than one node, each once. */
  duplicates: string[];
  /** `staging.*` nodes (not parts: excluded from picking and framing). */
  staging: THREE.Object3D[];
}

/** The node name as authored (before GLTFLoader's sanitising). */
export function originalName(obj: THREE.Object3D): string {
  const n: unknown = obj.userData.name;
  return typeof n === 'string' ? n : obj.name;
}

export function isStagingNode(obj: THREE.Object3D): boolean {
  const n = originalName(obj);
  return n === 'staging' || n.startsWith('staging.') || (typeof obj.userData.name !== 'string' && /^staging/.test(obj.name));
}

/** The `partId` extra on this object itself, or null. */
export function ownPartId(obj: THREE.Object3D): string | null {
  const id: unknown = obj.userData.partId;
  return typeof id === 'string' && id.trim() !== '' ? id : null;
}

/** Walk up `parent` from a hit object to the first ancestor (or itself) carrying a partId. */
export function partIdOf(obj: THREE.Object3D | null, stopAt?: THREE.Object3D): string | null {
  for (let o = obj; o && o !== stopAt; o = o.parent) {
    if (isStagingNode(o)) return null;
    const id = ownPartId(o);
    if (id) return id;
  }
  return null;
}

/**
 * Collect the part nodes under `root`: the outermost nodes with `userData.partId`.
 * `staging.*` subtrees are reported separately and never searched for parts.
 */
export function collectPartNodes(root: THREE.Object3D): PartNodes {
  const parts = new Map<string, THREE.Object3D[]>();
  const duplicates = new Set<string>();
  const staging: THREE.Object3D[] = [];
  const visit = (obj: THREE.Object3D) => {
    if (obj !== root && isStagingNode(obj)) {
      staging.push(obj);
      return;
    }
    const id = ownPartId(obj);
    if (id) {
      const list = parts.get(id);
      if (list) {
        list.push(obj);
        duplicates.add(id);
      } else parts.set(id, [obj]);
      return;
    }
    for (const child of obj.children) visit(child);
  };
  visit(root);
  return { parts, partIds: [...parts.keys()], duplicates: [...duplicates], staging };
}

/** Every texture referenced by a material's properties. */
export function texturesOf(material: THREE.Material): THREE.Texture[] {
  const out: THREE.Texture[] = [];
  for (const value of Object.values(material)) if (value instanceof THREE.Texture) out.push(value);
  return out;
}

export const materialList = (m: THREE.Material | THREE.Material[]): THREE.Material[] => (Array.isArray(m) ? m : [m]);

/** Dispose geometries, materials and textures under `root` (e.g. a loaded glTF scene). */
export function disposeObject(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((o) => {
    if (o instanceof THREE.Mesh || o instanceof THREE.Line || o instanceof THREE.Points) {
      geometries.add(o.geometry as THREE.BufferGeometry);
      for (const m of materialList(o.material as THREE.Material | THREE.Material[])) materials.add(m);
    }
  });
  const textures = new Set<THREE.Texture>();
  for (const m of materials) for (const t of texturesOf(m)) textures.add(t);
  for (const g of geometries) g.dispose();
  for (const m of materials) m.dispose();
  for (const t of textures) {
    const data: unknown = t.source?.data;
    t.dispose();
    if (typeof ImageBitmap !== 'undefined' && data instanceof ImageBitmap) data.close();
  }
}
